import { Suspense, lazy, type ComponentType } from 'react'
import { Navigate, createBrowserRouter } from 'react-router-dom'
import { AppLayout } from './layouts/AppLayout'
import { RequireAuth, RequireSuperAdmin } from './components/RequireAuth'
import { Login } from './routes/Login'
import { NotFound } from './routes/NotFound'
import { SkeletonRows } from './components/ui'
import { RouteError } from './components/RouteError'

/**
 * Each page is its own chunk: the shell, sign-in and shared libraries load
 * up front, everything else on first visit. An ops person opening the queue
 * doesn't download the admin console, the onboarding flow or the chart code.
 *
 * Pages keep named exports (CLAUDE.md: "named export matching the file"); this
 * adapts them to React.lazy's default-export shape.
 */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  const Page = lazy<ComponentType>(() => load().then((m) => ({ default: m[name] })))
  return (
    <Suspense fallback={<SkeletonRows rows={5} />}>
      <Page />
    </Suspense>
  )
}

/**
 * The MVP-1 pages (spec §8) plus the phase-2 admin console. Deliberately
 * absent: a shipments LIST page (the orders queue is the workflow), customers,
 * reports, map, integrations and billing UI.
 *
 * /track/:awb is NOT here. It is server-rendered by the api service so it ships
 * none of this dashboard's JavaScript to a customer on a 3G phone.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  // Public: a brand-new invitee has no session yet. There is no signup — access is invite-only.
  { path: '/invite', element: page(() => import('./routes/AcceptInvite'), 'AcceptInvite'), errorElement: <RouteError /> },
  // Old signup bookmarks go to sign-in, not a 404 after login.
  { path: '/signup', element: <Navigate to="/login" replace /> },
  {
    // Platform-admin console. A super admin usually belongs to no org.
    path: '/admin',
    element: (
      <RequireAuth allowNoOrg>
        <RequireSuperAdmin>{page(() => import('./layouts/AdminLayout'), 'AdminLayout')}</RequireSuperAdmin>
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/admin/orgs" replace /> },
      { path: 'orgs', element: page(() => import('./routes/admin/AdminOrgs'), 'AdminOrgs') },
      { path: 'orgs/new', element: page(() => import('./routes/admin/AdminOrgNew'), 'AdminOrgNew') },
      { path: 'orgs/:orgId', element: page(() => import('./routes/admin/AdminOrgDetail'), 'AdminOrgDetail') },
      { path: 'status-map', element: page(() => import('./routes/admin/AdminStatusMap'), 'AdminStatusMap') },
    ],
  },
  {
    path: '/onboarding/:step?',
    element: <RequireAuth>{page(() => import('./routes/Onboarding'), 'Onboarding')}</RequireAuth>,
    errorElement: <RouteError />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: page(() => import('./routes/Dashboard'), 'Dashboard') },
      { path: 'orders', element: page(() => import('./routes/Orders'), 'Orders') },
      { path: 'orders/:orderId', element: page(() => import('./routes/OrderDetail'), 'OrderDetail') },
      { path: 'shipments/:shipmentId', element: page(() => import('./routes/ShipmentDetail'), 'ShipmentDetail') },
      { path: 'alerts', element: page(() => import('./routes/Alerts'), 'Alerts') },
      { path: 'analytics/couriers', element: page(() => import('./routes/CourierScorecard'), 'CourierScorecard') },
      { path: 'analytics/rto', element: page(() => import('./routes/RtoAnalytics'), 'RtoAnalytics') },
      { path: 'settings/thresholds', element: page(() => import('./routes/Thresholds'), 'Thresholds') },
      { path: 'settings/stores', element: page(() => import('./routes/Stores'), 'Stores') },
      { path: 'settings/tracking-page', element: page(() => import('./routes/TrackingPageSettings'), 'TrackingPageSettings') },
      { path: 'settings/team', element: page(() => import('./routes/Team'), 'Team') },
      { path: '*', element: <NotFound /> },
    ],
  },
])
